import {
  Component,
  ElementRef,
  Input,
  OnChanges,
  SimpleChanges,
  ViewChild,
  AfterViewInit,
  Output,
  EventEmitter,
  OnDestroy,
} from '@angular/core';
import { createEmbeddingContext } from 'amazon-quicksight-embedding-sdk';
import {
  DashboardContentOptions,
  DashboardExperience
} from 'amazon-quicksight-embedding-sdk/dist/index';

@Component({
  selector: 'app-quicksight-display',
  templateUrl: './quicksight-display.component.html',
  styleUrls: ['./quicksight-display.component.scss'],
  standalone: false
})
export class QuicksightDisplayComponent implements OnChanges, AfterViewInit, OnDestroy {
  @Input() embedUrl: string;
  @Input() set dashboardOptions (options: DashboardContentOptions) {
    if (this.dashboardExperience) {
      this.loadOptions(options);
    }
    this.qsDashboardOptions = options;
  };

  @Output() dashboardLoaded = new EventEmitter<DashboardExperience>();
  @Output() sheetChanged = new EventEmitter<string>();

  @ViewChild('qsContainer', { static: true }) qsContainer: ElementRef;

  public embeddingContext: any;
  public dashboardExperience: DashboardExperience;
  public qsDashboardOptions: DashboardContentOptions = {};
  public isLoading = false;

  ngAfterViewInit(): void {
    if (this.embedUrl) {
      this.loadEmbed();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['embedUrl'] && !changes['embedUrl'].firstChange) {
      this.loadEmbed();
    }
  }

  ngOnDestroy(): void {
    const elems = Array.from(document.getElementsByClassName('quicksight-embedding-iframe'));
    for (let el of elems) {
      el.remove();
    }
  }

  loadOptions(options: DashboardContentOptions) {
    if (this.dashboardExperience) {
      if (options.sheetOptions) {
        this.isLoading = true;
        Promise.all([
          this.dashboardExperience.setSelectedSheetId(options.sheetOptions?.initialSheetId),
          this.dashboardExperience.setParameters(options.parameters ?? [])
        ]).finally(() => this.isLoading = false);
      }
    }
  }

  async loadEmbed() {
    if (!this.embedUrl || !this.qsContainer) return;

    this.qsContainer.nativeElement.innerHTML = '';

    const frameOptions = {
      container: this.qsContainer.nativeElement,
      url: this.embedUrl,
      resizeHeightOnSizeChangedEvent: true,
    };

    this.embeddingContext = await createEmbeddingContext();
    this.dashboardExperience = await this.embeddingContext.embedDashboard(frameOptions, this.qsDashboardOptions);
    this.dashboardExperience.addEventListener('CONTENT_LOADED', async (event) => {
      const sheetId = await this.dashboardExperience.getSelectedSheetId();
      const sheets = await this.dashboardExperience.getSheets();
      const sheetInfo = sheets.find(s => s.SheetId === sheetId);
      this.sheetChanged.emit(sheetInfo?.Name);
    });
    this.dashboardExperience.addEventListener('SELECTED_SHEET_CHANGED', (event) => {
      this.sheetChanged.emit((<any>event.message)?.selectedSheet.Name);
    });
    this.dashboardLoaded.emit(this.dashboardExperience);
  }
}
